// Fix WH80 (2026-10-15): factura de un proveedor NO registrado (de una sola vez).
// Corporativo sube el PDF y el XML, escribe el nombre del proveedor, elige la
// sucursal (o Corporativo) y una descripcion. No se da de alta ningun proveedor:
// la factura queda "Por validar" y sigue el camino de las facturas sin OC.
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, FileUp, FileText, UserPlus } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useTranslation } from "react-i18next";

export default function EventualInvoiceModal({ onClose, onSuccess }) {
  const { t } = useTranslation();
  const [pdfFile, setPdfFile] = useState(null);
  const [xmlFile, setXmlFile] = useState(null);
  const [xmlData, setXmlData] = useState(null);
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    api.get("/branches").then((r) => setBranches(r.data || [])).catch(() => toast.error(t("common.error")));
  }, [t]);

  const handleXml = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setXmlFile(f);
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const xml = new DOMParser().parseFromString(ev.target.result, "text/xml");
        const tfd = xml.getElementsByTagName("tfd:TimbreFiscalDigital")[0];
        const emisor = xml.getElementsByTagName("cfdi:Emisor")[0] || xml.getElementsByTagName("Emisor")[0];
        const data = {
          uuid: tfd?.getAttribute("UUID") || "",
          total: parseFloat(xml.documentElement.getAttribute("Total") || "0"),
          folio: xml.documentElement.getAttribute("Folio") || "",
          fecha: xml.documentElement.getAttribute("Fecha") || "",
          rfc: emisor?.getAttribute("Rfc") || "",
          nombre: emisor?.getAttribute("Nombre") || "",
        };
        setXmlData(data);
        // El nombre del emisor del CFDI sirve de sugerencia; se puede cambiar.
        if (data.nombre) setName((n) => n || data.nombre);
      } catch {
        toast.error("Error al parsear XML CFDI");
      }
    };
    reader.readAsText(f);
  };

  const canSubmit = !!pdfFile && !!xmlFile && !!branchId && name.trim().length >= 3;

  const upload = async () => {
    if (!pdfFile || !xmlFile) { toast.error(t("invoiceModal.filesRequired")); return; }
    if (name.trim().length < 3) { toast.error(t("invoices.eventualNameRequired")); return; }
    if (!branchId) { toast.error(t("invoiceModal.branchRequired")); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("pdf", pdfFile);
      fd.append("xml", xmlFile);
      fd.append("branch_id", branchId);
      fd.append("supplier_name", name.trim());
      fd.append("description", description.trim());
      await api.post("/invoices/upload-eventual", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("invoices.eventualUploaded"));
      onSuccess?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("invoiceModal.uploadError"));
    } finally {
      setUploading(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
        <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
          data-testid="invoice-eventual-modal" className="bg-white rounded-2xl w-full max-w-xl overflow-hidden max-h-[92vh] flex flex-col">
          <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="w-10 h-10 rounded-xl bg-[#FFDF00] flex items-center justify-center">
                <UserPlus className="w-5 h-5 text-[#1A1A1A]" />
              </div>
              <div>
                <h2 className="font-display font-bold text-lg">{t("invoices.eventualTitle")}</h2>
                <p className="text-xs text-slate-500">{t("invoices.eventualSubtitle")}</p>
              </div>
            </div>
            <button onClick={onClose} data-testid="eventual-close-btn" className="w-9 h-9 rounded-xl hover:bg-slate-100 flex items-center justify-center">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="p-6 space-y-4 overflow-y-auto">
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">{t("invoices.eventualName")}</label>
              <input data-testid="eventual-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={200}
                placeholder={t("invoices.eventualNamePh")}
                className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]" />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">{t("invoices.eventualBranch")}</label>
              <select data-testid="eventual-branch" value={branchId} onChange={(e) => setBranchId(e.target.value)}
                className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]">
                <option value="">{t("invoiceModal.selectBranchPh")}</option>
                {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">{t("invoices.eventualDescription")}</label>
              <textarea data-testid="eventual-description" value={description} onChange={(e) => setDescription(e.target.value)}
                rows={2} maxLength={500} placeholder={t("invoices.eventualDescriptionPh")}
                className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#FFDF00]" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <input id="eventual-pdf" data-testid="eventual-pdf" type="file" accept=".pdf"
                  onChange={(e) => setPdfFile(e.target.files[0])} className="hidden" />
                <label htmlFor="eventual-pdf" className={`flex flex-col items-center justify-center gap-2 px-4 py-6 rounded-xl border-2 border-dashed cursor-pointer transition ${
                  pdfFile ? "border-emerald-400 bg-emerald-50" : "border-slate-300 hover:border-[#FFDF00]"
                }`}>
                  <FileText className={`w-7 h-7 ${pdfFile ? "text-emerald-600" : "text-slate-400"}`} />
                  <div className="text-center">
                    <div className="text-xs font-bold">{pdfFile ? pdfFile.name : t("invoiceModal.uploadPdf")}</div>
                    <div className="text-[10px] text-slate-500 mt-0.5">{t("invoiceModal.pdfDesc")}</div>
                  </div>
                </label>
              </div>
              <div>
                <input id="eventual-xml" data-testid="eventual-xml" type="file" accept=".xml"
                  onChange={handleXml} className="hidden" />
                <label htmlFor="eventual-xml" className={`flex flex-col items-center justify-center gap-2 px-4 py-6 rounded-xl border-2 border-dashed cursor-pointer transition ${
                  xmlFile ? "border-emerald-400 bg-emerald-50" : "border-slate-300 hover:border-[#FFDF00]"
                }`}>
                  <FileText className={`w-7 h-7 ${xmlFile ? "text-emerald-600" : "text-slate-400"}`} />
                  <div className="text-center">
                    <div className="text-xs font-bold">{xmlFile ? xmlFile.name : t("invoiceModal.uploadXml")}</div>
                    <div className="text-[10px] text-slate-500 mt-0.5">{t("invoiceModal.xmlDesc")}</div>
                  </div>
                </label>
              </div>
            </div>

            {xmlData && (
              <div className="bg-slate-50 rounded-xl p-4 space-y-2 text-xs" data-testid="eventual-xml-data">
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">{t("invoiceModal.autoExtracted")}</div>
                <div className="flex justify-between"><span>{t("invoiceModal.uuid")}</span><span className="font-mono text-[10px]">{xmlData.uuid}</span></div>
                <div className="flex justify-between"><span>{t("invoiceModal.fiscalFolio")}</span><span className="font-mono">{xmlData.folio}</span></div>
                {xmlData.rfc && (
                  <div className="flex justify-between"><span>{t("invoiceModal.issuerRfc")}</span><span className="font-mono">{xmlData.rfc}</span></div>
                )}
                <div className="flex justify-between"><span>{t("invoiceModal.issuedAt")}</span><span>{xmlData.fecha?.split("T")[0]}</span></div>
                <div className="flex justify-between font-bold pt-1 border-t border-slate-200">
                  <span>{t("invoiceModal.total")}</span>
                  <span className="font-mono">${xmlData.total.toFixed(2)}</span>
                </div>
              </div>
            )}

            <p className="text-[11px] text-slate-500">{t("invoices.eventualHint")}</p>
          </div>

          <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-2">
            <button onClick={onClose} className="px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-semibold hover:bg-slate-100">
              {t("common.cancel")}
            </button>
            <button data-testid="eventual-submit-btn" onClick={upload} disabled={uploading || !canSubmit}
              className="btn-primary-chizy px-5 py-2 rounded-xl text-sm flex items-center gap-1.5 disabled:opacity-50">
              <FileUp className="w-4 h-4" /> {uploading ? t("invoiceModal.uploading") : t("invoiceModal.upload")}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
